// Paleta ÚNICA de la oficina (04-11, web/qa/auditoria-apple/visual.md §2.A): la leen tailwind.config.js (las clases
// `oficina-*` de la interfaz) y el lienzo de la escena (mundo/dibujar.ts, lib/oficina/pixel-art.ts). Antes el lienzo
// tenía su propia paleta (marrones, oro decorativo, azul de ventanas) y la escena parecía un juego con pegatinas encima.
// JS plano (sin tipos) para que tailwind.config.js lo importe tal cual; los tipos están en paleta.d.ts.
// Documentada en docs/design-system.md §Oficina: un valor nuevo aquí va también allí, en el mismo commit.

/**
 * Lenguaje de macOS/iPadOS en oscuro (grises de sistema de Apple, colores de sistema para los estados) con el oro de
 * Venuz como acento: el oro significa «actúa» (botones, permisos) y nunca se pinta en el lienzo.
 */
export const oficina = {
  fondo: '#0B0B0C', // fondo de la oficina (negro Venuz)
  panel: '#161618', // barras, lista del equipo, cabeceras
  elevada: '#1C1C1E', // ventanas, hojas, tarjetas (gris de sistema de Apple, nivel 1)
  control: '#2C2C2E', // controles, campos, burbuja del agente (nivel 2)
  hover: '#2C2C2E',
  pulsado: '#3A3A3C', // pulsado / hover sobre un control (nivel 3)
  activa: '#3A300E', // fila seleccionada (oro sobre el panel)
  borde: '#050506',
  linea: '#2C2C2E', // separadores y bordes de campo
  texto: '#F5F5F7',
  texto2: '#AEAEB2',
  texto3: '#8E8E93',
  // Sobre el oro va siempre tinta oscura (acento-tinta, ≥ 10:1), nunca blanco.
  acento: '#F0B90B',
  'acento-hover': '#F8C93A',
  'acento-tinta': '#0B0B0C', // texto sobre el oro (≥ 10:1)
  enlace: '#F5C842',
  'burbuja-pau': '#3A2F0B',
  'burbuja-agente': '#2C2C2E',
  // Pelotas v7 (01-11, docs/pelotas/diseno/v7): el panel privado «Tu agente · Solo lo ves tú» al lado del
  // chat con Santi: el fondo con el tinte de las burbujas de Pau, y su cabecera/compositor más oscuros.
  tuyo: '#1B170A',
  'tuyo-oscuro': '#141107',
  'tuyo-globo': '#26211A', // la burbuja del agente dentro del panel privado
  'meta-pau': '#E6CF8A', // hora y ✓ dentro de la burbuja de Pau
  'te-necesita-claro': '#F6DE8A', // texto sobre fondo «te necesita»
  'error-claro': '#F8C4C4',
  // La marca de Venuz (el cuadrado dorado de la barra superior).
  marca: '#F0B90B',
  'marca-oscura': '#B88A06',
  'marca-tinta': '#1A1307',
  // Estados de un agente (lo mismo en la escena, la lista y el chat).
  trabajando: '#30D158',
  'trabajando-tinta': '#06240F', // icono sobre la insignia verde
  'te-necesita': '#F0B90B',
  // Naranja de aviso (S10: escena, subidas, avisos). NO es el «no puede seguir sin ti» de la lista S12, que
  // es violeta: situacion-bloqueado.
  bloqueado: '#FF9F0A',
  error: '#FF453A',
  cerrando: '#BF5AF2',
  esperando: '#8E8E93',
  // S12 — las situaciones de la lista (marco/situacion.ts), iguales en la lista, el chat y la franja. Cada una:
  // su color, el tono claro del texto dentro de una fila teñida y la tinta oscura del icono sobre la insignia.
  'situacion-bloqueado': '#BF5AF2', // 2 · no puede seguir sin ti (violeta de sistema de Apple)
  'situacion-bloqueado-claro': '#E0B3F8',
  'situacion-bloqueado-tinta': '#1E0B2A',
  'situacion-problema': '#C8B8A0', // 4 · problema que arregla la oficina (arena, nunca rojo)
  'situacion-problema-tinta': '#231C12',
  'situacion-problema-pau': '#FF6961', // 4 · problema en el que Pau tiene que actuar (rojo)
  'situacion-problema-pau-tinta': '#2A0B0B',
  'situacion-apagado': '#636366', // 7 · punto de «sin nada pendiente» en la escena
  // Con Santi (pelota en su lado): el cian de sistema de Apple, tranquilo; el mismo en la lista, la oficina y su tablero.
  santi: '#64D2FF',
  'santi-tinta': '#04202B', // icono sobre la insignia cian
  // Cerebros de Calypso en la Sala de Control (abril usa marca-oscura). Validado con la paleta
  // de gráficas sobre «elevada»: se distinguen también con daltonismo y contrastan ≥ 3:1.
  'cerebro-azul': '#3987E5',
  'cerebro-rosa': '#D55181',
  // Partidas de la pestaña Gasto (rediseño aprobado por Pau, 07-10-2026): sin naranja ni ámbar, que quedan para
  // el estado frente al objetivo. 1 Conversación · 2 Repetir respuestas que fallaron · 3 Revisor de seguridad ·
  // 4 Memoria y mensajes automáticos · 5 Fotos y otros (5 = texto3: también es el gris único de los días estimados).
  'serie-1': '#3987E5',
  'serie-2': '#9085E9',
  'serie-3': '#199E70',
  'serie-4': '#D55181',
  'serie-5': '#8F8D86',
  // Gasto: días estimados que no son el elegido (gris apagado) y huecos discontinuos («sin datos», «en curso»).
  'gasto-apagado': '#4A4945',
  'gasto-hueco': '#3A3A40',
  // Gráficas del informe visual (03-11, features/oficina/informe): orden fijo, validado sobre «panel» (banda de
  // luminosidad, daltonismo y contraste ≥ 3:1). Una sola serie va en el oro de la marca (acento).
  'informe-1': '#C98500',
  'informe-2': '#3987E5',
  'informe-3': '#D95926',
  'informe-4': '#9085E9',
};

/**
 * Lo que solo pinta el lienzo de la escena. Es la misma escalera de grises que la interfaz (`oficina.*`); aquí van solo
 * los peldaños que la interfaz no usa. Ni oro ni color de sala: en la escena el color es del DOM (insignias, puntos).
 */
export const escena = {
  suelo: '#121214', // baldosa de 32×32 del suelo…
  sueloAlt: '#141416', // …y su vecina, casi igual: el suelo se lee liso
  mesaLuz: '#48484A', // canto de luz del tablero
  mesaVacia: '#232325', // silueta de una mesa sin nadie
  isla: '#222224', // 04-11 — la placa de un grupo de mesas («mesa de equipo»), un peldaño sobre la sala (`elevada`)
  huecoIsla: '#2C2C2E', // …y el sitio vacío que deja alguien dentro de un grupo (la silueta, sobre la placa)
  papel: '#D1D1D6', // la hoja en las manos de quien lee (rayas en oficina.texto3)
  sombra: 'rgba(0,0,0,0.30)', // sombra bajo silla y mesa
};
